// ============================================================================
// combos.js — Benjen's attack graph, DW4-style. Pure data consumed by the
// player state machine. Each node is one attack "move":
//
//   dur       total move length (s)
//   cancel    [start,end] window (s) in which the next input is accepted
//   move      forward lunge {t0,t1,speed} (metres/s) applied during the move
//   hits      array of hit windows: { t0,t1, radius, arc(halfAngle), dmg,
//             reaction, knock(impulse), launch(vY), token:new-each-window,
//             heavy(bool → longer hitstop) }
//   next      id of the follow-up on a Normal press (null = chain ends)
//   charge    id branched to on a Charge press (null = none)
//   sweep     true → 360° AoE
//   shockwave {t, dmg, knock, range} → emit a travelling black wave at time t
//
// The chain:  N1→N2→N3→N4→N5→N6   with charge branches off each:
//   N1-C launcher · N2-C stun · N3-C sweep · N4-C knockback · N5-C finisher wave
// ============================================================================

import { RE_FLINCH, RE_LAUNCH, RE_KNOCK, RE_STUN } from '../core/constants.js';

const A = Math.PI; // full arc helper

export const NODES = {
  // ---------- Normal chain ----------
  N1: {
    id: 'N1', name: 'Draw Cut', dur: 0.40, cancel: [0.22, 0.55],
    move: { t0: 0.05, t1: 0.20, speed: 6 },
    hits: [{ t0: 0.14, t1: 0.24, radius: 3.0, arc: 0.9, dmg: 14, reaction: RE_FLINCH, knock: 3 }],
    next: 'N2', charge: 'C1',
  },
  N2: {
    id: 'N2', name: 'Return Slash', dur: 0.40, cancel: [0.22, 0.55],
    move: { t0: 0.04, t1: 0.18, speed: 5 },
    hits: [{ t0: 0.13, t1: 0.23, radius: 3.0, arc: 1.0, dmg: 15, reaction: RE_FLINCH, knock: 3 }],
    next: 'N3', charge: 'C2',
  },
  N3: {
    id: 'N3', name: 'Rising Arc', dur: 0.44, cancel: [0.24, 0.6],
    move: { t0: 0.05, t1: 0.2, speed: 5 },
    hits: [{ t0: 0.15, t1: 0.26, radius: 3.1, arc: 1.1, dmg: 16, reaction: RE_FLINCH, knock: 4 }],
    next: 'N4', charge: 'C3',
  },
  N4: {
    id: 'N4', name: 'Cross Reap', dur: 0.46, cancel: [0.26, 0.62],
    move: { t0: 0.05, t1: 0.2, speed: 5 },
    hits: [{ t0: 0.16, t1: 0.28, radius: 3.2, arc: 1.25, dmg: 18, reaction: RE_FLINCH, knock: 5 }],
    next: 'N5', charge: 'C4',
  },
  N5: {
    id: 'N5', name: 'Spiral Cut', dur: 0.5, cancel: [0.28, 0.66],
    move: { t0: 0.05, t1: 0.22, speed: 5 },
    hits: [{ t0: 0.17, t1: 0.32, radius: 3.3, arc: 1.6, dmg: 20, reaction: RE_KNOCK, knock: 7 }],
    next: 'N6', charge: 'C5',
  },
  N6: {
    id: 'N6', name: 'Ashfall', dur: 0.72, cancel: [0.5, 0.9],
    move: { t0: 0.06, t1: 0.24, speed: 6 },
    hits: [{ t0: 0.22, t1: 0.4, radius: 3.6, arc: A, dmg: 30, reaction: RE_KNOCK, knock: 11, heavy: true }],
    next: null, charge: null,
  },

  // ---------- Charge branches ----------
  C1: { // N-C launcher
    id: 'C1', name: 'Ember Rise', dur: 0.62, cancel: [0.42, 0.8],
    move: { t0: 0.06, t1: 0.2, speed: 4 },
    hits: [{ t0: 0.2, t1: 0.34, radius: 3.0, arc: 1.0, dmg: 20, reaction: RE_LAUNCH, launch: 9, knock: 2 }],
    next: null, charge: null,
  },
  C2: { // NN-C stun
    id: 'C2', name: 'Hilt Crash', dur: 0.66, cancel: [0.46, 0.85],
    move: { t0: 0.06, t1: 0.22, speed: 5 },
    hits: [{ t0: 0.24, t1: 0.36, radius: 3.0, arc: 1.1, dmg: 22, reaction: RE_STUN, knock: 3 }],
    next: null, charge: null,
  },
  C3: { // NNN-C sweep AoE
    id: 'C3', name: 'Cinder Sweep', dur: 0.78, cancel: [0.5, 0.95],
    move: { t0: 0.05, t1: 0.2, speed: 3 },
    hits: [{ t0: 0.26, t1: 0.46, radius: 4.2, arc: A, dmg: 24, reaction: RE_KNOCK, knock: 8, heavy: true }],
    next: null, charge: null,
  },
  C4: { // NNNN-C heavy knockback
    id: 'C4', name: 'Ruin Hammer', dur: 0.86, cancel: [0.58, 1.05],
    move: { t0: 0.08, t1: 0.26, speed: 7 },
    hits: [{ t0: 0.3, t1: 0.46, radius: 3.8, arc: 1.5, dmg: 34, reaction: RE_KNOCK, knock: 16, launch: 3, heavy: true }],
    next: null, charge: null,
  },
  C5: { // NNNNN-C finisher wave — Masamune's black shockwave
    id: 'C5', name: 'BLACK MASAMUNE', dur: 1.15, cancel: [0.85, 1.35],
    move: { t0: 0.1, t1: 0.3, speed: 4 },
    hits: [{ t0: 0.34, t1: 0.5, radius: 4.6, arc: A, dmg: 40, reaction: RE_KNOCK, knock: 14, heavy: true }],
    shockwave: { t: 0.42, dmg: 55, knock: 22, range: 26 },
    next: null, charge: null,
  },

  // ---------- Aerial (from a jump) ----------
  JUMP: {
    id: 'JUMP', name: 'Falling Cut', dur: 0.5, cancel: [0.3, 0.7],
    move: null,
    hits: [{ t0: 0.12, t1: 0.3, radius: 3.2, arc: 1.4, dmg: 22, reaction: RE_KNOCK, knock: 9, heavy: true }],
    next: null, charge: null,
  },
};

// entry points
export const START_NORMAL = 'N1';
export const START_CHARGE = 'C1';
